import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ProductGrid } from '../components/ProductGrid';
import { SaleItemsGrid } from '../components/SaleItemsGrid';
import { CatalogModeToggle } from '../components/CatalogModeToggle';
import { CatalogFilters } from '../components/CatalogFilters';
import { CatalogSort } from '../components/CatalogSort';
import { MobileFilterDrawer } from '../components/MobileFilterDrawer';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useLocationStore } from '@/stores/useLocationStore';
import type { RentalMode } from '@/stores/useLocationStore';
import { useInfiniteProducts } from '@/hooks/useCatalog';
import { useItemsForSale } from '@/hooks/useSales';
import { useLocationBySlug } from '@/hooks/useLocations';
import { useSearchParams, useParams, useNavigate } from 'react-router';
import { useLocale } from '@/i18n/useLocale';
import { buildPublishedLocaleUrl } from '@/i18n/routing';
import type { CustomerLocationDto } from '@/types';
import { applyCatalogueClientFilters } from './Catalog.filters';

type CatalogMode = 'rent' | 'buy';

type CatalogFilterState = {
    categories?: string[];
    ageRanges?: string[];
    brands?: string[];
    query: string;
    isAvailable?: boolean;
};

export interface CatalogProps {
    /**
     * When the route is /:locale/catalogue/:locationSlug, the route loader
     * resolves the slug → location and passes the result here. The component
     * uses it to preselect the location in the store on first render.
     * (SEO is now driven entirely by each route shell's `meta` export.)
     *
     * `undefined` also covers the case where the build-time loader never ran
     * for this slug (ssr:false loaders only run at build time — see
     * CLAUDE.md); when that happens, the component resolves the slug itself
     * client-side via `useLocationBySlug` — see `effectivePreselectedLocation`
     * below.
     */
    preselectedLocation?: CustomerLocationDto;
}

export const Catalog = ({ preselectedLocation }: CatalogProps = {}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { location, dateRange, rentalMode, setRentalMode, setLocation } = useLocationStore();
    const { locationSlug } = useParams<{ locationSlug?: string }>();
    const navigate = useNavigate();
    const localeState = useLocale();
    const seoLocale = localeState?.locale ?? 'en';

    // Build a locale-aware path with the page's current query string preserved.
    const navigateWithSearch = useCallback(
        (relativePath: string) => {
            const search = searchParams.toString();
            const url = `${buildPublishedLocaleUrl(seoLocale, relativePath)}${search ? `?${search}` : ''}`;
            navigate(url, { replace: true });
        },
        [navigate, searchParams, seoLocale],
    );

    // Resolve /:locale/catalogue/:locationSlug client-side when the build-time
    // loader never ran for this slug: ssr:false loaders only run at BUILD time,
    // for the paths react-router.config.ts's prerender() enumerated (see
    // CLAUDE.md). A location created after the last deploy has no static
    // loader output, so the route falls through to the client SPA shell with
    // `preselectedLocation` undefined. `useLocationBySlug` (staleTime 0 +
    // refetchOnMount 'always', see app/hooks/useLocations.ts) also keeps a
    // loader-resolved location fresh in the background — it revalidates even
    // when `preselectedLocation` already arrived from the loader, so this
    // fetch runs unconditionally whenever there's a slug.
    const {
        data: bySlugLocation,
        isLoading: isResolvingLocationSlug,
        isSuccess: hasResolvedLocationSlug,
    } = useLocationBySlug(locationSlug);
    const effectivePreselectedLocation = preselectedLocation ?? bySlugLocation ?? undefined;
    // Only true on the un-prerendered-slug fallback path: no build-time data
    // AND the client fetch hasn't settled yet. A loader-resolved slug never
    // hits this — `preselectedLocation` is already set on first render.
    const isResolvingUnknownSlug = !preselectedLocation && !!locationSlug && isResolvingLocationSlug;
    // The loader didn't resolve it AND the live API confirms no such location
    // exists (a settled fetch that resolved to `null`, not merely errored) —
    // redirect to the bare catalogue instead of stranding the user on a page
    // that can never preselect a location.
    const slugConfirmedUnknown =
        !preselectedLocation && !!locationSlug && hasResolvedLocationSlug && bySlugLocation === null;

    // Tracks whether the URL→store sync has completed for the current locationSlug.
    // Without this, the store→URL effect fires on the initial render (with the
    // store still empty/persisted) and races the URL→store sync — incorrectly
    // bouncing /catalogue/<slug> back to /catalogue before the slug resolves.
    const [initialSyncDone, setInitialSyncDone] = useState(false);

    // URL → store: mirror the resolved location (loader-provided or resolved
    // client-side above) into the location store.
    useEffect(() => {
        if (!locationSlug || !effectivePreselectedLocation) {
            setInitialSyncDone(false);
            return;
        }
        if (location?.id !== effectivePreselectedLocation.id) {
            setLocation(effectivePreselectedLocation);
        }
        setInitialSyncDone(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSlug, effectivePreselectedLocation]);

    // Store → URL: once the URL→store sync has finished, mirror subsequent store
    // changes (user picked a different location, or cleared it) back into the URL.
    // Only acts when the URL already has a slug — picking a location on the bare
    // /catalogue does not push a slug into the URL.
    useEffect(() => {
        if (!initialSyncDone || !locationSlug) return;
        if (location?.slug === locationSlug) return;
        if (!location?.slug) {
            navigateWithSearch('/catalogue');
            return;
        }
        navigateWithSearch(`/catalogue/${location.slug}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.slug, initialSyncDone]);

    // Live-confirmed-unknown slug (see slugConfirmedUnknown above): bounce to
    // the bare catalogue instead of rendering a page that can never
    // preselect a location.
    useEffect(() => {
        if (!slugConfirmedUnknown) return;
        navigateWithSearch('/catalogue');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slugConfirmedUnknown]);

    // Strip any legacy ?page= param from the URL — the catalogue now uses
    // infinite scroll instead of pagination, so the parameter is meaningless.
    // We only strip on mount to avoid fighting other URL writes during the
    // session; subsequent location changes start a fresh infinite query
    // (the query key includes locationId, so cached pages are discarded).
    useEffect(() => {
        if (searchParams.has('page')) {
            const params = new URLSearchParams(searchParams);
            params.delete('page');
            setSearchParams(params, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [mode, setMode] = useState<CatalogMode>('rent');
    const categoryFromUrl = searchParams.get('category') || searchParams.get('categories') || undefined;
    const parsedCategoriesFromUrl = useMemo(
        () => (categoryFromUrl ? categoryFromUrl.split(',').filter(Boolean) : undefined),
        [categoryFromUrl]
    );

    const [filters, setFilters] = useState<CatalogFilterState>({
        categories: searchParams.get('categories')?.split(',').filter(Boolean) ||
            parsedCategoriesFromUrl,
        ageRanges: searchParams.get('ageRanges')?.split(',').filter(Boolean) || undefined,
        brands: searchParams.get('brands')?.split(',').filter(Boolean) || undefined,
        query: searchParams.get('query') || '',
    });

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            categories: parsedCategoriesFromUrl
        }));
    }, [parsedCategoriesFromUrl]);

    // Sync ?mode= URL param to rentalMode store on initial load
    // Allows links like /catalog?mode=subscription to land in the correct filter state
    useEffect(() => {
        const modeParam = searchParams.get('mode') as RentalMode | null;
        if (modeParam && ['all', 'short-term', 'subscription'].includes(modeParam)) {
            setRentalMode(modeParam);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [sort, setSort] = useState({
        sortBy: 'recommended',
        sortOrder: 'asc'
    });

    const recommendedDateContext = useMemo(() => {
        if (!dateRange.from || !dateRange.to) {
            return {};
        }

        return {
            startDate: dateRange.from.toISOString(),
            endDate: dateRange.to.toISOString(),
        };
    }, [dateRange.from, dateRange.to]);

    // Infinite-scroll page size. Larger batches mean fewer round-trips when
    // the user keeps scrolling on mobile; smaller batches keep first-paint
    // light. 12 mirrors the prior pagination page size.
    const pageSize = 12;
    const hasAvailabilityContext = !!(location && dateRange.from && dateRange.to && dateRange.to > dateRange.from);

    const {
        data: rentalData,
        isLoading: isLoadingRentals,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteProducts({
        locationId: location?.id,
        ...recommendedDateContext,
        categories: filters.categories?.join(','),
        ageRanges: filters.ageRanges?.join(','),
        brands: filters.brands?.join(','),
        query: filters.query,
        isAvailable: hasAvailabilityContext ? filters.isAvailable : undefined,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        pageSize,
    });

    // Flatten the infinite pages into a single array. The hook's queryKey
    // includes all filter params, so changing filters discards the prior
    // pages and refetches from page 1 — no stale data leaks across queries.
    const allRentalProducts = useMemo(
        () => rentalData?.pages.flatMap((p) => p?.data ?? []) ?? [],
        [rentalData],
    );
    // Server-side totalCount across the full result set (unfiltered by the
    // client-side rentalMode / availability checks below). We still surface it
    // as the "items count" header — it's the best approximation of the size of
    // the user's filter set even when client-side trimming hides a few items.
    const totalRentalItems = rentalData?.pages[0]?.totalCount ?? 0;

    const { data: saleData, isLoading: isLoadingSales } = useItemsForSale({
        locationId: location?.id,
        page: 1,
        pageSize,
        enabled: !!location?.id,
    });

    const handleFilterChange = useCallback((newFilters: Partial<CatalogFilterState>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => ({ ...prev, query: e.target.value }));
    }, []);

    const handleModeChange = useCallback((newMode: CatalogMode) => {
        setMode(newMode);
        // Strip any leftover ?page= from a previous session (the catalogue is
        // now infinite-scroll). No-op when the URL is already clean.
        if (searchParams.has('page')) {
            const params = new URLSearchParams(searchParams);
            params.delete('page');
            setSearchParams(params);
        }
    }, [searchParams, setSearchParams]);

    const activeFilterCount = [
        filters.categories?.length ? 1 : 0,
        filters.ageRanges?.length ? 1 : 0,
        filters.brands?.length ? 1 : 0,
        filters.isAvailable ? 1 : 0,
    ].filter(Boolean).length;

    const isLoading = mode === 'rent' ? isLoadingRentals : isLoadingSales;

    const filteredRentalProducts = useMemo(
        () => applyCatalogueClientFilters(allRentalProducts, { rentalMode, location, onlyAvailable: filters.isAvailable && hasAvailabilityContext }),
        [allRentalProducts, rentalMode, location, filters.isAvailable, hasAvailabilityContext],
    );

    const emptyStateContent = useMemo(() => {
        const heading = 'No equipment matches these filters';

        if (filters.isAvailable && location?.id && dateRange.from && dateRange.to) {
            return {
                heading,
                body: 'Nothing is currently available for the selected location and dates. Try changing dates, location, or removing a few filters.',
            };
        }

        if (filters.query?.trim()) {
            return {
                heading,
                body: 'No equipment matched your search. Try a broader term or clear a few filters.',
            };
        }

        return {
            heading,
            body: 'Try clearing a few filters, changing location, or switching to another sort to see more options.',
        };
    }, [dateRange.from, dateRange.to, filters.isAvailable, filters.query, location?.id]);

    const totalItems = mode === 'rent'
        ? totalRentalItems
        : (saleData?.totalCount ?? 0);

    // Header count: for rental mode, show the actual NUMBER OF CARDS RENDERED
    // (post all client-side filtering) — not the server's totalCount, which
    // counts items the location-stock SQL filter passes through even when
    // they're fully booked for the user's dates. Append "+" while more pages
    // remain on the server so users know the number might grow. For buy mode
    // we still trust the server count (no equivalent client-side trimming).
    const visibleRentalCount = filteredRentalProducts.length;
    const headerCountLabel = useMemo(() => {
        if (mode !== 'rent') {
            return totalItems > 0
                ? `${totalItems.toLocaleString()} ${totalItems === 1 ? 'item' : 'items'}`
                : null;
        }
        if (visibleRentalCount === 0) return null;
        const noun = visibleRentalCount === 1 ? 'item' : 'items';
        const suffix = hasNextPage ? '+' : '';
        return `${visibleRentalCount.toLocaleString()}${suffix} ${noun}`;
    }, [mode, totalItems, visibleRentalCount, hasNextPage]);

    // IntersectionObserver-backed infinite scroll. The sentinel below the
    // grid auto-triggers fetchNextPage when it enters the viewport, so the
    // user gets a continuous list on both mobile and desktop. The "Load more"
    // button remains as an explicit fallback for users on slow networks, in
    // reduced-motion modes, or who never scroll past the trigger.
    const infiniteScrollSentinelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const node = infiniteScrollSentinelRef.current;
        if (!node || mode !== 'rent') return;
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            // 1200px rootMargin pre-fetches the next page well before the
            // user reaches the end. The sentinel sits at the bottom of the
            // grid (above the Load More button), so on average we start
            // loading while the user is still ~2 product rows away.
            { rootMargin: '1200px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [mode, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const catalogTitle = (() => {
        const locationSuffix = location ? ` in ${location.name}` : '';
        if (mode === 'buy') return `Pre-loved baby gear${locationSuffix}`;
        if (rentalMode === 'subscription') return `Monthly baby equipment plans${locationSuffix}`;
        if (rentalMode === 'short-term') return `Short-term baby equipment rentals${locationSuffix}`;
        return `Baby equipment rentals${locationSuffix}`;
    })();

    // Un-prerendered-slug fallback: show an accessible loading state instead
    // of a flash of the bare (no-preselection) catalogue while resolving, or
    // while the confirmed-unknown-slug redirect effect above is committing.
    if (isResolvingUnknownSlug || slugConfirmedUnknown) {
        return (
            <div
                className="flex min-h-[50vh] items-center justify-center"
                role="status"
                aria-live="polite"
                aria-label="Loading location"
            >
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <>
            {/*
              * SEO for both /catalogue and /catalogue/:locationSlug is emitted
              * from the route shells' `meta` exports (RR v7 meta descriptors).
              * No helmet path remains here.
              */}

        <div className="min-h-screen bg-background">
                <div className="border-b border-border bg-background">
                    <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">

                        {/* Title row */}
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 lg:mb-0">
                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                                <h1 className="text-xl font-bold text-foreground">{catalogTitle}</h1>
                                {!isLoading && headerCountLabel && (
                                    <span className="text-sm text-muted-foreground">
                                        {headerCountLabel}
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                <CatalogModeToggle mode={mode} itemsForSaleCount={saleData?.totalCount ?? 0} onChange={handleModeChange} />
                                {mode === 'rent' && <div className="hidden lg:block"><CatalogSort sortBy={sort.sortBy} sortOrder={sort.sortOrder}
                                    onSortChange={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })} /></div>}
                            </div>
                        </div>

                        {mode === 'rent' && (
                            <div className="flex min-w-0 items-center justify-between gap-2 lg:hidden" data-testid="catalog-mobile-controls">
                                <MobileFilterDrawer filters={filters} onChange={handleFilterChange}
                                    onSearchChange={handleSearchChange} activeFilterCount={activeFilterCount} locationId={location?.id} />
                                <CatalogSort sortBy={sort.sortBy} sortOrder={sort.sortOrder}
                                    onSortChange={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="mx-auto max-w-7xl px-4 py-4 sm:py-8 sm:px-6 lg:px-8 lg:py-10">
                    {mode === 'rent' ? (
                        <div className="grid grid-cols-1 gap-x-10 gap-y-10 lg:grid-cols-[290px_minmax(0,1fr)]">
                            <aside className="hidden lg:block" aria-label="Catalog filters">
                                <div className="sticky top-40 space-y-4">
                                    <div className="rounded-[1.75rem] border border-border/80 bg-white/92 p-6 shadow-card">
                                        <CatalogFilters
                                            filters={filters}
                                            onChange={handleFilterChange}
                                            onSearchChange={handleSearchChange}
                                            locationId={location?.id}
                                        />
                                    </div>
                                </div>
                            </aside>

                            <section
                                className="lg:col-span-1"
                                id="catalog-content"
                                role="tabpanel"
                                aria-label="Rental products"
                                aria-live="polite"
                                aria-busy={isLoading ? 'true' : 'false'}
                            >
                                {filters.isAvailable && !hasAvailabilityContext && (
                                    <p className="mb-4 text-sm text-muted-foreground" role="status">
                                        Choose a location and rental dates to filter by availability. Showing all matching products for now.
                                    </p>
                                )}
                                <ProductGrid
                                    products={filteredRentalProducts}
                                    isLoading={isLoadingRentals}
                                    emptyStateHeading={emptyStateContent.heading}
                                    emptyStateBody={emptyStateContent.body}
                                />

                                {/* Skeleton row visible while the next page is
                                    loading — gives the user explicit "more
                                    coming" feedback even on fast connections. */}
                                {isFetchingNextPage && (
                                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
                                        {[...Array(3)].map((_, i) => (
                                            <div
                                                key={i}
                                                className="overflow-hidden rounded-[1.75rem] border border-border/70 bg-[linear-gradient(180deg,rgba(255,255,255,1)_0%,rgba(249,245,239,1)_100%)] shadow-card"
                                            >
                                                <div className="delight-skeleton aspect-[4/5]" />
                                                <div className="space-y-3 p-5">
                                                    <div className="delight-skeleton h-3 w-1/3 rounded-full" />
                                                    <div className="delight-skeleton h-5 w-4/5 rounded-full" />
                                                    <div className="delight-skeleton h-4 w-3/5 rounded-full" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* IntersectionObserver sentinel — sits at the
                                    BOTTOM OF THE GRID (above the Load More
                                    button) so the next page starts loading
                                    while the user can still see content. */}
                                <div
                                    ref={infiniteScrollSentinelRef}
                                    aria-hidden="true"
                                    className="h-1 w-full"
                                />
                            </section>
                        </div>
                    ) : (
                        <section
                            id="catalog-content"
                            role="tabpanel"
                            aria-label="Items for sale"
                            aria-live="polite"
                            aria-busy={isLoading ? 'true' : 'false'}
                        >
                            <SaleItemsGrid
                                items={saleData?.items ?? []}
                                isLoading={isLoadingSales}
                            />
                        </section>
                    )}

                    {/* Manual fallback for the infinite scroll. The sentinel
                        lives at the bottom of the grid above (so the
                        IntersectionObserver triggers while the user is still
                        in the grid). This button stays visible as a backup
                        for users on reduced-motion / slow networks / who
                        scroll faster than the prefetch can keep up. */}
                    {mode === 'rent' && !isLoading && filteredRentalProducts.length > 0 && (
                        <div className="mt-10 flex flex-col items-center gap-3">
                            {hasNextPage ? (
                                <Button
                                    variant="outline"
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                    className="min-h-[44px] min-w-[200px] gap-2"
                                    aria-label="Load more products"
                                >
                                    {isFetchingNextPage ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            Loading more…
                                        </>
                                    ) : (
                                        'Load more products'
                                    )}
                                </Button>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    You&apos;ve reached the end of the catalogue.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
